/**
 * AssemblyTree.jsx — collapsible assembly / sub-assembly / unit tree panel.
 *
 * Geometry truth is the static manifest; this component only renders the tree
 * that `buildTree()` derived from it. It makes NO assumption about depth: the
 * hierarchy is ragged (A1 has units directly beneath it, A3 has all three
 * levels, A2/A4..A9 bottom out at S-level), so:
 *
 *   - indentation comes from `node.depth` (whatever it happens to be),
 *   - the expand twisty is rendered only when `node.children.length > 0`,
 *   - the level filter filters on `node.level` (A/S/U), never on depth.
 *
 * Presentational + local UI state only. Selection / isolation / visibility are
 * owned by the parent and arrive as props.
 *
 * Props
 *   tree             object   result of buildTree() (roots, byId, childrenOf, ...)
 *   selectedId       string|null
 *   isolatedId       string|null   currently soloed node, or null
 *   hiddenIds        Set|Array     explicitly hidden node ids
 *   onSelect(id)
 *   onHover(id|null)
 *   onIsolate(id|null)             null = clear isolate
 *   onToggleVisible(id, nextVisible)
 */

import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  LEVEL_ASSEMBLY,
  LEVEL_SUBASSEMBLY,
  LEVEL_UNIT,
} from './assemblyTree.js';
import './AssemblyTree.css';

const LEVEL_RANK = {
  [LEVEL_ASSEMBLY]: 0,
  [LEVEL_SUBASSEMBLY]: 1,
  [LEVEL_UNIT]: 2,
};
const rankOf = (level) => (LEVEL_RANK[level] ?? 2);

const LEVEL_BADGE = {
  [LEVEL_ASSEMBLY]: 'A',
  [LEVEL_SUBASSEMBLY]: 'S',
  [LEVEL_UNIT]: 'U',
};

const LEVEL_FILTERS = [
  { rank: 0, label: 'Assemblies' },
  { rank: 1, label: '+ Sub-assemblies' },
  { rank: 2, label: 'All levels' },
];

const EMPTY_TREE = {
  roots: [],
  byId: new Map(),
  order: [],
  childrenOf: () => [],
  ancestorsOf: () => [],
  descendantsOf: () => [],
  parentOf: () => null,
  depthOf: () => -1,
  nodes: [],
  diagnostics: [],
};

const toSet = (v) => (v instanceof Set ? v : new Set(Array.isArray(v) ? v : []));

function AssemblyTree({
  tree,
  selectedId = null,
  isolatedId = null,
  hiddenIds,
  onSelect,
  onHover,
  onIsolate,
  onToggleVisible,
}) {
  const t = tree && tree.byId ? tree : EMPTY_TREE;
  const hidden = useMemo(() => toSet(hiddenIds), [hiddenIds]);

  const [expanded, setExpanded] = useState(() => new Set());
  const [query, setQuery] = useState('');
  const [maxRank, setMaxRank] = useState(2);
  const [focusId, setFocusId] = useState(null);

  const rowRefs = useRef(new Map());
  const wantFocus = useRef(false);

  // Open the top level whenever a new tree arrives (manifest load / reload).
  useEffect(() => {
    setExpanded(new Set(t.roots));
    setFocusId(t.roots[0] ?? null);
  }, [t]);

  // Reveal the current selection without disturbing anything else.
  useEffect(() => {
    if (!selectedId || !t.byId.has(selectedId)) return;
    const anc = t.ancestorsOf(selectedId);
    if (anc.length === 0) return;
    setExpanded((prev) => {
      if (anc.every((id) => prev.has(id))) return prev;
      const next = new Set(prev);
      anc.forEach((id) => next.add(id));
      return next;
    });
  }, [selectedId, t]);

  // ---- search ------------------------------------------------------------
  const q = query.trim().toLowerCase();
  const { keep, autoExpand } = useMemo(() => {
    if (!q) return { keep: null, autoExpand: null };
    const k = new Set();
    const auto = new Set();
    for (const node of t.byId.values()) {
      const name = String(node.name ?? '').toLowerCase();
      const id = String(node.id ?? '').toLowerCase();
      if (!name.includes(q) && !id.includes(q)) continue;
      k.add(node.id);
      for (const a of t.ancestorsOf(node.id)) { k.add(a); auto.add(a); }
      // Keep the matched branch browsable, but leave it collapsed by default.
      for (const d of t.descendantsOf(node.id)) k.add(d);
    }
    return { keep: k, autoExpand: auto };
  }, [q, t]);

  const isOpen = useCallback(
    (id) => expanded.has(id) || (autoExpand ? autoExpand.has(id) : false),
    [expanded, autoExpand],
  );

  // ---- flatten to visible rows (ragged-depth aware) ----------------------
  const rows = useMemo(() => {
    const out = [];
    const visit = (id) => {
      const node = t.byId.get(id);
      if (!node) return;
      if (rankOf(node.level) > maxRank) return;   // level filter, NOT depth
      if (keep && !keep.has(id)) return;

      const kids = t.childrenOf(id).filter((cid) => {
        const c = t.byId.get(cid);
        if (!c || rankOf(c.level) > maxRank) return false;
        return !keep || keep.has(cid);
      });
      const open = kids.length > 0 && isOpen(id);
      out.push({
        id,
        node,
        depth: node.depth,
        hasChildren: kids.length > 0,
        childCount: kids.length,
        open,
      });
      if (open) kids.forEach(visit);
    };
    t.roots.forEach(visit);
    return out;
  }, [t, maxRank, keep, isOpen]);

  // Keep the roving-tabindex target on a row that still exists.
  useEffect(() => {
    if (rows.length === 0) return;
    if (focusId && rows.some((r) => r.id === focusId)) return;
    setFocusId(rows.some((r) => r.id === selectedId) ? selectedId : rows[0].id);
  }, [rows, focusId, selectedId]);

  useEffect(() => {
    if (!wantFocus.current || !focusId) return;
    wantFocus.current = false;
    const el = rowRefs.current.get(focusId);
    if (el) {
      el.focus();
      el.scrollIntoView({ block: 'nearest' });
    }
  }, [focusId, rows]);

  const moveFocus = (id) => { wantFocus.current = true; setFocusId(id); };

  const toggle = useCallback((id, force) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      const open = next.has(id) || (autoExpand ? autoExpand.has(id) : false);
      const want = force === undefined ? !open : force;
      if (want) next.add(id); else next.delete(id);
      return next;
    });
  }, [autoExpand]);

  const expandAll = () => setExpanded(new Set(t.order));
  const collapseAll = () => setExpanded(new Set());

  // ---- derived visual state ----------------------------------------------
  const hiddenByAncestor = useCallback((id) => {
    if (hidden.has(id)) return true;
    return t.ancestorsOf(id).some((a) => hidden.has(a));
  }, [hidden, t]);

  const dimmedByIsolate = useCallback((id) => {
    if (!isolatedId || !t.byId.has(isolatedId)) return false;
    if (id === isolatedId) return false;
    if (t.ancestorsOf(id).includes(isolatedId)) return false;
    if (t.ancestorsOf(isolatedId).includes(id)) return false;
    return true;
  }, [isolatedId, t]);

  // ---- keyboard ----------------------------------------------------------
  const onKeyDown = (e, row, index) => {
    const { id, hasChildren, open } = row;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (index < rows.length - 1) moveFocus(rows[index + 1].id);
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (index > 0) moveFocus(rows[index - 1].id);
        break;
      case 'ArrowRight':
        e.preventDefault();
        if (hasChildren && !open) toggle(id, true);
        else if (hasChildren && open && index < rows.length - 1) moveFocus(rows[index + 1].id);
        break;
      case 'ArrowLeft': {
        e.preventDefault();
        if (hasChildren && open) { toggle(id, false); break; }
        const parent = t.parentOf(id);
        if (parent && rows.some((r) => r.id === parent)) moveFocus(parent);
        break;
      }
      case 'Home':
        e.preventDefault();
        if (rows.length) moveFocus(rows[0].id);
        break;
      case 'End':
        e.preventDefault();
        if (rows.length) moveFocus(rows[rows.length - 1].id);
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        onSelect?.(id);
        break;
      case 'Escape':
        if (isolatedId) { e.preventDefault(); onIsolate?.(null); }
        break;
      default:
        break;
    }
  };

  const total = t.order.length;

  return (
    <div className="asmtree" onMouseLeave={() => onHover?.(null)}>
      <div className="asmtree__head">
        <h3 className="asmtree__title">Assembly</h3>
        <span className="asmtree__count">
          {rows.length}
          {rows.length === total ? '' : ` / ${total}`}
        </span>
      </div>

      <div className="asmtree__controls">
        <input
          className="asmtree__search"
          type="search"
          value={query}
          placeholder="Filter parts…"
          aria-label="Filter parts by name"
          onChange={(e) => setQuery(e.target.value)}
        />
        <div className="asmtree__levels" role="group" aria-label="Level filter">
          {LEVEL_FILTERS.map((f) => (
            <button
              key={f.rank}
              type="button"
              className={`asmtree__level-btn${maxRank === f.rank ? ' is-active' : ''}`}
              aria-pressed={maxRank === f.rank}
              onClick={() => setMaxRank(f.rank)}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="asmtree__bulk">
          <button type="button" className="asmtree__bulk-btn" onClick={expandAll}>Expand all</button>
          <button type="button" className="asmtree__bulk-btn" onClick={collapseAll}>Collapse all</button>
          {isolatedId && (
            <button
              type="button"
              className="asmtree__bulk-btn is-warn"
              onClick={() => onIsolate?.(null)}
            >
              Clear isolate
            </button>
          )}
        </div>
      </div>

      <div className="asmtree__list" role="tree" aria-label="Vehicle assembly hierarchy">
        {rows.length === 0 && (
          <p className="asmtree__empty">
            {total === 0 ? 'No assembly manifest loaded.' : 'Nothing matches this filter.'}
          </p>
        )}

        {rows.map((row, index) => {
          const { id, node, depth, hasChildren, open, childCount } = row;
          const selected = id === selectedId;
          const isolated = id === isolatedId;
          const selfHidden = hidden.has(id);
          const invisible = hiddenByAncestor(id);
          const dimmed = dimmedByIsolate(id) || invisible;
          const cls = [
            'asmtree__row',
            selected ? 'is-selected' : '',
            isolated ? 'is-isolated' : '',
            dimmed ? 'is-dimmed' : '',
            `is-level-${node.level}`,
          ].filter(Boolean).join(' ');

          return (
            <div
              key={id}
              role="treeitem"
              className={cls}
              aria-level={depth + 1}
              aria-selected={selected}
              aria-expanded={hasChildren ? open : undefined}
              tabIndex={focusId === id ? 0 : -1}
              ref={(el) => {
                if (el) rowRefs.current.set(id, el);
                else rowRefs.current.delete(id);
              }}
              style={{ '--asmtree-indent': `${depth * 14}px` }}
              onKeyDown={(e) => onKeyDown(e, row, index)}
              onFocus={() => setFocusId(id)}
              onMouseEnter={() => onHover?.(id)}
            >
              <span className="asmtree__indent" aria-hidden="true" />

              {hasChildren ? (
                <button
                  type="button"
                  tabIndex={-1}
                  className="asmtree__twisty"
                  aria-label={`${open ? 'Collapse' : 'Expand'} ${node.name}`}
                  onClick={(e) => { e.stopPropagation(); toggle(id); }}
                >
                  <span className={`asmtree__chev${open ? ' is-open' : ''}`} aria-hidden="true" />
                </button>
              ) : (
                <span className="asmtree__twisty asmtree__twisty--leaf" aria-hidden="true" />
              )}

              <span className={`asmtree__badge asmtree__badge--${node.level}`} aria-hidden="true">
                {LEVEL_BADGE[node.level] ?? '?'}
              </span>

              <button
                type="button"
                tabIndex={-1}
                className="asmtree__label"
                title={id}
                onClick={() => onSelect?.(id)}
                onFocus={() => onHover?.(id)}
              >
                <span className="asmtree__name">{node.name}</span>
                {hasChildren && <span className="asmtree__kids">{childCount}</span>}
              </button>

              <span className="asmtree__actions">
                <button
                  type="button"
                  tabIndex={-1}
                  className={`asmtree__act${isolated ? ' is-on' : ''}`}
                  aria-pressed={isolated}
                  aria-label={isolated ? `Stop isolating ${node.name}` : `Isolate ${node.name}`}
                  title={isolated ? 'Stop isolating' : 'Isolate (solo)'}
                  onClick={(e) => { e.stopPropagation(); onIsolate?.(isolated ? null : id); }}
                >
                  ◎
                </button>
                <button
                  type="button"
                  tabIndex={-1}
                  className={`asmtree__act${selfHidden ? ' is-off' : ''}`}
                  aria-pressed={selfHidden}
                  aria-label={selfHidden ? `Show ${node.name}` : `Hide ${node.name}`}
                  title={selfHidden ? 'Show' : 'Hide'}
                  onClick={(e) => { e.stopPropagation(); onToggleVisible?.(id, selfHidden); }}
                >
                  {selfHidden ? '○' : '●'}
                </button>
              </span>
            </div>
          );
        })}
      </div>

      <p className="asmtree__provenance">Representative geometry — not OEM CAD</p>
    </div>
  );
}

/**
 * Memoised: the explode slider fires up to ~100 setT() per drag, and every one
 * of those re-rendered all ~57 rows plus two ancestor walks per row. All props
 * from ExplodedView are stable across a `t` change (useMemo tree, state ids,
 * empty-dep useCallbacks), so this skips the render entirely.
 */
export default memo(AssemblyTree);
